import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, AlertTriangle, TrendingUp, Package, Truck, RefreshCw,
  CheckCircle, Clock, XCircle, Plus, ArrowRight, Bell, Settings,
  Fuel, Wrench, CircleDot, DollarSign, BarChart3, FileText, Filter } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI } from '../hooks/useFleetData';

const API_URL = API_BASE;

const priorityColors = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  pr_created: 'bg-blue-100 text-blue-700',
  po_created: 'bg-purple-100 text-purple-700',
  ordered: 'bg-indigo-100 text-indigo-700',
  received: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-700'
};

const FleetProcurementPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('overview');
  const [lowStockParts, setLowStockParts] = useState([]);
  const [procurementRequests, setProcurementRequests] = useState([]);
  const [consumptionData, setConsumptionData] = useState(null);
  const [alertConfigs, setAlertConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [lowStock, requests, consumption, alerts] = await Promise.all([
      fetchAPI('/procurement/low-stock'),
      fetchAPI('/procurement/requests'),
      fetchAPI('/consumption/summary'),
      fetchAPI('/alerts/configs')]
      );
      setLowStockParts(lowStock);
      setProcurementRequests(requests);
      setConsumptionData(consumption);
      setAlertConfigs(alerts);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoReorder = async () => {
    try {
      const result = await fetchAPI('/procurement/auto-reorder', { method: 'POST' });
      alert(`Created ${result.requests?.length || 0} procurement requests`);
      loadData();
    } catch (err) {
      console.error('Auto-reorder failed:', err);
    }
  };

  const handleCreatePR = async (requestId) => {
    try {
      await fetchAPI(`/procurement/requests/${requestId}/create-pr`, { method: 'POST' });
      loadData();
    } catch (err) {
      console.error('Failed to create PR:', err);
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const result = await fetchAPI('/alerts/check', { method: 'POST' });
      alert(`Generated ${result.alerts_generated || 0} alerts`);
      loadData();
    } catch (err) {
      console.error('Alert check failed:', err);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="procurement-page-title">{t("fleetProcurementInventory", "Fleet Procurement & Inventory")}

          </h1>
          <p className="text-gray-500">{t("managePartsFuel", "Manage parts, fuel, tires - integrated with procurement")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" size="sm" onClick={handleCheckAlerts} className="text-amber-600 border-amber-200">
            <Bell className="w-4 h-4 mr-2" />{t("checkAlerts", "Check Alerts")}

          </Button>
          <Button className="bg-red-500 hover:bg-red-600 text-white" size="sm" onClick={handleAutoReorder}>
            <ShoppingCart className="w-4 h-4 mr-2" />{t("autoreorder", "Auto-Reorder")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("lowStockItems", "Low Stock Items")}</p>
                <p className="text-2xl font-bold text-red-600">{lowStockParts.length}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("pendingRequests", "Pending Requests")}</p>
                <p className="text-2xl font-bold text-amber-600">
                  {procurementRequests.filter((r) => r.status === 'pending').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("totalSpend30d", "Total Spend (30d)")}</p>
                <p className="text-2xl font-bold text-green-600">{t("r", "R$")}
                  {consumptionData?.totals?.total_cost?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("activeAlerts", "Active Alerts")}</p>
                <p className="text-2xl font-bold text-blue-600">{alertConfigs.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white p-1 rounded-lg shadow-sm">
          <TabsTrigger value="overview" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />{t("overview", "Overview")}

          </TabsTrigger>
          <TabsTrigger value="low-stock" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <AlertTriangle className="w-4 h-4 mr-2" />{t("lowStock", "Low Stock (")}
            {lowStockParts.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <ShoppingCart className="w-4 h-4 mr-2" />{t("procurement", "Procurement (")}
            {procurementRequests.length})
          </TabsTrigger>
          <TabsTrigger value="consumption" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />{t("consumption", "Consumption")}

          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-2" />{t("alertConfig", "Alert Config")}

          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Breakdown */}
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />{t("costBreakdown30", "Cost Breakdown (30 days)")}

                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Fuel className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">{t("fuel", "Fuel")}</span>
                  </div>
                  <span className="font-bold text-blue-600">{t("r", "R$")}
                    {consumptionData?.totals?.fuel_cost?.toLocaleString() || '0'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-orange-600" />
                    <span className="font-medium">{t("parts", "Parts")}</span>
                  </div>
                  <span className="font-bold text-orange-600">{t("r", "R$")}
                    {consumptionData?.totals?.parts_cost?.toLocaleString() || '0'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Wrench className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">{t("maintenance", "Maintenance")}</span>
                  </div>
                  <span className="font-bold text-purple-600">{t("r", "R$")}
                    {consumptionData?.totals?.maintenance_cost?.toLocaleString() || '0'}
                  </span>
                </div>
                <div className="border-t pt-4 flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{t("total", "Total")}</span>
                  <span className="text-xl font-bold text-gray-900">{t("r", "R$")}
                    {consumptionData?.totals?.total_cost?.toLocaleString() || '0'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Consumption */}
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Truck className="w-5 h-5 text-gray-600" />{t("topSpendingBy", "Top Spending by Vehicle")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                {consumptionData?.summary?.length > 0 ?
                <div className="space-y-3">
                    {consumptionData.summary.slice(0, 5).map((item, idx) =>
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold">
                            {idx + 1}
                          </span>
                          <span className="font-medium">{t("vehicle", "Vehicle #")}{item.vehicle_id}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{t("r", "R$")}{item.total_cost.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">
                            {item.fuel_transactions}{t("refuels", "refuels \u2022")}{item.maintenance_count}{t("maint", "maint.")}
                      </p>
                        </div>
                      </div>
                  )}
                  </div> :

                <div className="text-center py-8 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>{t("noConsumptionData", "No consumption data yet")}</p>
                  </div>
                }
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Low Stock Tab */}
        <TabsContent value="low-stock">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />{t("lowStockItems", "Low Stock Items")}

                </CardTitle>
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={handleAutoReorder}>
                  <ShoppingCart className="w-4 h-4 mr-2" />{t("autoreorderAll", "Auto-Reorder All")}

                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {lowStockParts.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500 opacity-50" />
                  <h3 className="text-lg font-medium text-gray-900">{t("allStockLevels", "All Stock Levels OK")}</h3>
                  <p>{t("noItemsAre", "No items are below their reorder point")}</p>
                </div> :

              <div className="divide-y">
                  {lowStockParts.map((part) =>
                <div key={part.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{part.name}</h4>
                          <p className="text-sm text-gray-500">
                            {part.part_number} • {part.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-600">{part.quantity_on_hand}</p>
                          <p className="text-xs text-gray-500">{t("onHand", "On Hand")}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-medium text-gray-600">{part.reorder_point}</p>
                          <p className="text-xs text-gray-500">{t("reorderPoint", "Reorder Point")}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-red-500">-{part.shortage}</p>
                          <p className="text-xs text-gray-500">{t("shortage", "Shortage")}</p>
                        </div>
                        <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200"
                      onClick={() => {/* Create single PR */}}>
                      
                          <Plus className="w-4 h-4 mr-1" />{t("order", "Order")}
                      {part.reorder_quantity}
                        </Button>
                      </div>
                    </div>
                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Procurement Requests Tab */}
        <TabsContent value="requests">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-blue-500" />{t("procurementRequests", "Procurement Requests")}

                </CardTitle>
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />{t("newRequest", "New Request")}

                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {procurementRequests.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noProcurementRequests", "No Procurement Requests")}</h3>
                  <p>{t("createARequest", "Create a request or use auto-reorder")}</p>
                </div> :

              <div className="divide-y">
                  {procurementRequests.map((req) =>
                <div key={req.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{req.request_code}</h4>
                            <Badge className={statusColors[req.status]}>{req.status.replace('_', ' ')}</Badge>
                            <Badge className={priorityColors[req.priority]}>{req.priority}</Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {req.part_name}{t("qty", "\u2022 Qty:")}{req.quantity_requested}
                          </p>
                          {req.notes && <p className="text-xs text-gray-400 mt-1">{req.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {req.status === 'pending' &&
                    <Button
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      onClick={() => handleCreatePR(req.id)}>
                      
                            <ArrowRight className="w-4 h-4 mr-1" />{t("createPr", "Create PR")}

                    </Button>
                    }
                        {req.pr_id &&
                    <Badge className="bg-purple-100 text-purple-700">{t("pr", "PR #")}
                      {req.pr_id}
                          </Badge>
                    }
                        {req.po_id &&
                    <Badge className="bg-indigo-100 text-indigo-700">{t("po", "PO #")}
                      {req.po_id}
                          </Badge>
                    }
                      </div>
                    </div>
                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consumption Tab */}
        <TabsContent value="consumption">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />{t("consumptionByVehicle", "Consumption by Vehicle")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {consumptionData?.summary?.length > 0 ?
              <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("vehicle", "Vehicle")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("fuelL", "Fuel (L)")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("fuelCost", "Fuel Cost")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("partsCost", "Parts Cost")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("maintCost", "Maint. Cost")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("total", "Total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consumptionData.summary.map((item, idx) =>
                    <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Truck className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{t("vehicle", "Vehicle #")}{item.vehicle_id}</span>
                            </div>
                          </td>
                          <td className="text-right py-3 px-4">{item.fuel_liters?.toLocaleString() || 0} L</td>
                          <td className="text-right py-3 px-4 text-blue-600">{t("r", "R$")}{item.fuel_cost?.toLocaleString() || 0}</td>
                          <td className="text-right py-3 px-4 text-orange-600">{t("r", "R$")}{item.parts_cost?.toLocaleString() || 0}</td>
                          <td className="text-right py-3 px-4 text-purple-600">{t("r", "R$")}{item.maintenance_cost?.toLocaleString() || 0}</td>
                          <td className="text-right py-3 px-4 font-bold">{t("r", "R$")}{item.total_cost?.toLocaleString() || 0}</td>
                        </tr>
                    )}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50">
                        <td className="py-3 px-4 font-bold">{t("total", "TOTAL")}</td>
                        <td className="text-right py-3 px-4 font-bold">
                          {consumptionData.summary.reduce((sum, i) => sum + (i.fuel_liters || 0), 0).toLocaleString()} L
                        </td>
                        <td className="text-right py-3 px-4 font-bold text-blue-600">{t("r", "R$")}
                        {consumptionData.totals?.fuel_cost?.toLocaleString() || 0}
                        </td>
                        <td className="text-right py-3 px-4 font-bold text-orange-600">{t("r", "R$")}
                        {consumptionData.totals?.parts_cost?.toLocaleString() || 0}
                        </td>
                        <td className="text-right py-3 px-4 font-bold text-purple-600">{t("r", "R$")}
                        {consumptionData.totals?.maintenance_cost?.toLocaleString() || 0}
                        </td>
                        <td className="text-right py-3 px-4 font-bold text-xl">{t("r", "R$")}
                        {consumptionData.totals?.total_cost?.toLocaleString() || 0}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div> :

              <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noConsumptionData", "No Consumption Data")}</h3>
                  <p>{t("addFuelLogs", "Add fuel logs and maintenance records to see consumption")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alert Config Tab */}
        <TabsContent value="alerts">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />{t("alertConfigurations", "Alert Configurations")}

                </CardTitle>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />{t("newAlertRule", "New Alert Rule")}

                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {alertConfigs.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <Bell className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noAlertRules", "No Alert Rules")}</h3>
                  <p>{t("createRulesTo", "Create rules to get notified when stock is low, maintenance is due, etc.")}</p>
                </div> :

              <div className="divide-y">
                  {alertConfigs.map((config) =>
                <div key={config.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    config.severity === 'critical' ? 'bg-red-100' : 'bg-amber-100'}`
                    }>
                          <Bell className={`w-6 h-6 ${
                      config.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`
                      } />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 capitalize">
                            {config.alert_type.replace('_', ' ')}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {config.entity_type}{t("threshold", "\u2022 Threshold:")}{config.threshold_value} {config.threshold_unit}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={config.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {config.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        {config.auto_create_pr &&
                    <Badge className="bg-blue-100 text-blue-700">{t("autopr", "Auto-PR")}</Badge>
                    }
                        <Button size="sm" variant="outline">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};

export default FleetProcurementPage;